import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { mergeMap } from 'rxjs';
import { ICreateDevice, IDevice } from 'src/app/shared/models/IDevice';
import { IDeviceType } from 'src/app/shared/models/IDevice-type';
import { ResourcesService } from 'src/app/shared/services/resources.service';
import { OnlySpacesValidator } from 'src/app/shared/util/onlySpaces.validator';
import { ValidationType } from '../../clinical/shared/clinical.enum';
import { DeviceService } from '../shared/device.service';
import { DeviceValidator } from '../shared/device.validator';

@Component({
  selector: 'app-add-device-facility',
  templateUrl: './add-device-facility.component.html',
  styleUrls: ['./add-device-facility.component.css']
})
export class AddDeviceFacilityComponent implements OnInit {
  isLoading: boolean = false;
  resourceKeyValue: any;
  resource: string = "";
  deviceTitle: string;
  createMode: boolean;
  deviceForm = this.fb.group({
    id: ['-1', []],
    deviceType: ['', [Validators.required]],
    deviceId: ['', [Validators.required, Validators.maxLength(200), OnlySpacesValidator.onlyWhiteSpace]]
  });
  selectedDeviceTypeId = 0;
  selectedDeviceTypeValue = "0";
  allDeviceTypes: IDeviceType[];
  allDeviceTypesWithModel: IDeviceType[];
  allDevices: IDevice[] = [];
  deviceValidationType = ValidationType.None;
  modalRef: NgbModalRef;

  @ViewChild(ToastContainerDirective, { static: true }) toastContainer: ToastContainerDirective;
  @ViewChild('deviceCreationModal') deviceCreationModal: TemplateRef<ElementRef>;

  constructor(private deviceService: DeviceService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private resourcesService: ResourcesService,
    public toastrService: ToastrService,
    private modalService: NgbModal) {
  }
  ngOnInit(): void {
    this.isLoading = true;
    this.toastrService.overlayContainer = this.toastContainer;
    this.resourcesService.bsObject
      .subscribe((result: any) => {
        result && (this.resourceKeyValue = result);
        this.resource = this.resourceKeyValue.resourceKeyDevice;
      });

    this.deviceService.GetAllDeviceTypes().pipe(
      mergeMap((deviceTypes: IDeviceType[]) => {
        if (deviceTypes && deviceTypes.length > 0) {
          this.allDeviceTypes = deviceTypes.filter(
            (deviceType, i, deviceTypesArray) => deviceTypesArray.findIndex(t => t.name === deviceType.name) === i
          );
          this.allDeviceTypesWithModel = deviceTypes;
        }
        const currentFacilityId = Number(localStorage.getItem('facilityId'));
        return this.deviceService.getAllDevices(currentFacilityId);
      }),
      mergeMap(devices => {
        if (devices && devices.length > 0) {
          this.allDevices = devices;
        }
        return this.route.params
      })).subscribe(params => {
        if (params['id'] && this.allDevices.length > 0) {
          var device = this.allDevices.find(device => device.id == params['id']);
          this.selectedDeviceTypeId = device?.deviceTypeID;
          this.deviceForm.patchValue({
            id: device?.id,
            deviceType: device?.deviceType,
            deviceId: device?.deviceID
          });
          this.createMode = false;
          this.deviceForm.controls.deviceType.disable();
        } else {
          this.createMode = true;
        }
        this.deviceTitle = this.createMode ? this.resourceKeyValue.createDevice : this.resourceKeyValue.editDevice;
        this.setDeviceValidations();
        this.isLoading = false;
      }, (error) => {
        this.setDeviceValidations();
        this.isLoading = false;
        console.error(error);
      });
  }
  addNewDevice() {
    this.isLoading = true;
    const deviceID = this.getTrimValue(this.deviceForm, 'deviceId');
    this.deviceService.getActiveDevicesCountByDeviceIdentifier(deviceID, this.selectedDeviceTypeId)
      .subscribe((activeDeviceCount: number) => {
        if (activeDeviceCount == 0) {
          this.deviceValidationType = ValidationType.None;
          this.createDeviceWorkflow();
        } else {
          //open popup
          this.isLoading = false;
          this.deviceValidationType = ValidationType.RegexMatchDeviceInDifferentFacility;
          this.openModal(this.deviceCreationModal);
        }
      });

  }
  createDeviceWorkflow() {
    this.isLoading = true;
    this.closeCreateDevice();
    this.deviceService.createAdminDevice(this.createRequestObject(this.deviceForm))
      .subscribe(response => {
        if (response) {
          this.router.navigate(['/devices']);
          this.isLoading = false;
          if (this.createMode) {
            this.showSuccessNotification(this.resourceKeyValue.deviceSuccessMessageOnCreate);
          } else {
            this.showSuccessNotification(this.resourceKeyValue.deviceSuccessMessageOnUpdate);
          }
        } else {
          throw new Error('Failed to create device');
        }
      }, (error: HttpErrorResponse) => {
        this.isLoading = false;
        //TODO: Add calling AI here.
        console.error(error);
        this.showErrorNotification(this.resourceKeyValue.deviceErrorMessageOnCreate);
      });
  }
  createRequestObject(formGroup: UntypedFormGroup): ICreateDevice {
    var deviceID = this.getTrimValue(formGroup, 'deviceId');
    var deviceModel = this.allDeviceTypesWithModel.find(x => new RegExp(x.regex).test(deviceID.trim()));
    let requestObj = {
      id: this.getTrimValue(formGroup, 'id'),
      facilityID: Number(localStorage.getItem('facilityId')),
      deviceID: this.getTrimValue(formGroup, 'deviceId'),
      deviceTypeID: this.selectedDeviceTypeId,
      deviceModelID: deviceModel?.deviceModelId,
      deviceAtlasKey: "",
      deviceTypeCode: deviceModel.code,
      type: this.deviceValidationType
    };
    return requestObj;
  }
  getTrimValue(formGroup: UntypedFormGroup, fieldName: string) {
    const val = formGroup.get(fieldName).value;
    if (typeof val === "number") {
      return val;
    }
    else if (val !== undefined && val !== null) {
      return val.trim();
    }
    return "";
  }

  showSuccessNotification(message: string) {
    this.toastrService.success(message, this.resource, {
      timeOut: 3000,
      closeButton: true,
      positionClass: 'toast-top-center',
    });
  }

  showErrorNotification(message: string) {
    this.toastrService.error(message, this.resource, {
      timeOut: 3000,
      closeButton: true,
      positionClass: 'toast-top-center',
    });
  }
  selectDeviceType(deviceTypeName: string) {
    const devicetype = this.allDeviceTypes.find(x => x.name.toLowerCase() == deviceTypeName.toLowerCase());
    this.selectedDeviceTypeId = devicetype?.id;
    this.resetControlValues();
  }
  setDeviceValidations(): void {
    this.deviceForm.controls.deviceId.setValidators(
      [
        Validators.required,
        Validators.maxLength(200),
        , OnlySpacesValidator.onlyWhiteSpace,
        DeviceValidator.ValidateDeviceID(this.allDevices, this.allDeviceTypesWithModel)
      ]);
  }
  resetControlValues() {
    this.setFieldValue('deviceId', '');
    this.setControlsUntoched(this.deviceForm);
  }
  setFieldValue(fieldName: string, fieldValue: any) {
    var val = this.deviceForm.get(fieldName);
    if (val) {
      val.setValue(fieldValue);
    }
  }
  setControlsUntoched(form: UntypedFormGroup) {
    Object.keys(form.controls).forEach((key) => {
      const control = form.controls[key];
      control.markAsPristine();
      control.markAsUntouched();
    });
  }
  closeCreateDevice(): void {
    if (this.deviceValidationType == ValidationType.RegexMatchDeviceInDifferentFacility) {
      this.modalRef.close();
    }
  }
  openModal(model: TemplateRef<ElementRef>) {
    this.modalRef = this.modalService.open(model, { backdrop: 'static', keyboard: false });
  }
}
